'use client'

import { useCallback, useEffect, useMemo } from 'react'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { uniqBy } from 'lodash'
import type { Review } from '@skop-app/lib-domain/types'
import { ReviewStatus, ReviewType } from '@skop-app/lib-types'

import { useReviewData } from '@/context/ReviewContext'
import { useUserContext } from '@/context/UserContext'
import { useUserInvits } from '@/services/queries/notification'
import { onCreateReviewMention } from '@/services/queries/message'
import { useStreamUserUnread } from '@/services/queries/streamUserUnread'
import {
  sortDate,
  transformInvitationToNotification,
  transformReviewMentionToNotification,
} from '@/utils'
import { getReviewMetadataValue } from '@/utils/reviews'
import type { ReviewMentionNotificationLike } from '@/utils/notifications.transformers'
import type { Notification } from '@/types'
import { reviewKeys } from '@/services/queries/review.keys'

// Notification type moved to '@/types/notification'

// Singleton subscription management to avoid duplicate websocket subscriptions
let activeReviewMentionSubscription: { unsubscribe: () => void } | null = null
let activeUserId: string | null = null
let subscriberCount = 0

export function useNotifications() {
  const queryClient = useQueryClient()
  const tAccount = useTranslations('Account')
  const { user, organizationScope } = useUserContext()
  const { allReviews = [] } = useReviewData()

  // Team and Projects invitations
  const { data: invitations } = useUserInvits({ userID: user?.id })

  const { unreadError: streamUnreadError, unreadOrderNotifications } =
    useStreamUserUnread(user?.id)

  const subscriptionReviewMentionCallback = useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: reviewKeys.drawerPrefix,
      refetchType: 'active',
    })
    void queryClient.invalidateQueries({
      queryKey: reviewKeys.notifications(
        organizationScope.activeOrganizationId,
        user?.id,
      ),
      refetchType: 'active',
    })
  }, [organizationScope.activeOrganizationId, queryClient, user?.id])

  const buildReviewNotificationHref = useCallback((review: Review): string => {
    const projectID = getReviewMetadataValue(review, 'projectID')
    const fieldOptionsId = getReviewMetadataValue(review, 'fieldOptionsId')
    const reviewParam = `reviewId=${review.id}`
    const nodeParam = `node=${review.elementID}`

    switch (review.type) {
      case ReviewType.REVIEW_PRODUCT:
        return projectID
          ? `/projects/ressources/${projectID}?${nodeParam}&${reviewParam}`
          : `/inventory?${nodeParam}&${reviewParam}`
      case ReviewType.REVIEW_OPTION:
        return fieldOptionsId
          ? `/references/options/${fieldOptionsId}?${nodeParam}&${reviewParam}`
          : `/references/options?${nodeParam}&${reviewParam}`
      case ReviewType.REVIEW_REFERENCE:
        return `/references?${nodeParam}&${reviewParam}`
      case ReviewType.REVIEW_HEADER:
        return projectID
          ? `/projects/catalog/${projectID}?${reviewParam}#${review.elementID}`
          : `/projects/catalog?${reviewParam}`
      default:
        return `/references?${nodeParam}&${reviewParam}`
    }
  }, [])

  // Subscribe to new messages, when the user is online
  useEffect(() => {
    if (!user?.id) return

    subscriberCount += 1

    // (Re)start subscription only if needed or user changed
    if (!activeReviewMentionSubscription || activeUserId !== user.id) {
      if (activeReviewMentionSubscription) {
        activeReviewMentionSubscription.unsubscribe()
        activeReviewMentionSubscription = null
      }

      activeReviewMentionSubscription = onCreateReviewMention({
        userMentionsId: user.id,
        subscriptionMentionCallback: subscriptionReviewMentionCallback,
      })
      activeUserId = user.id
    }

    return () => {
      subscriberCount -= 1
      if (subscriberCount <= 0) {
        activeReviewMentionSubscription?.unsubscribe()
        activeReviewMentionSubscription = null
        activeUserId = null
      }
    }
  }, [subscriptionReviewMentionCallback, user?.id])

  // Prepare data for notifications display
  const notifications: Notification[] = useMemo(() => {
    const items: Notification[] = []

    if (unreadOrderNotifications.length) {
      items.push(...uniqBy(unreadOrderNotifications, 'key'))
    }

    if (user?.id && allReviews.length) {
      const reviewMentionNotifications: ReviewMentionNotificationLike[] = []

      for (const review of allReviews) {
        if (review.status !== ReviewStatus.PENDING) continue

        for (const message of review.messages?.items ?? []) {
          const isMentioned = message.mentions?.items?.some(
            (mention) => mention.userMentionsId === user.id,
          )
          if (!isMentioned || message.userID === user.id) continue

          reviewMentionNotifications.push({
            id: message.id,
            reviewID: review.id,
            href: buildReviewNotificationHref(review),
            createdAt: message.createdAt,
            title: getReviewMetadataValue(review, 'title'),
            messageText: [
              tAccount('notification_review_mention'),
              message.user?.name ?? '',
              getReviewMetadataValue(review, 'title'),
            ]
              .filter(Boolean)
              .join(' · '),
            user: {
              id: message.user?.id ?? message.userID ?? '',
              name: message.user?.name ?? '',
            },
          })
        }
      }

      items.push(
        ...uniqBy(
          reviewMentionNotifications.map(transformReviewMentionToNotification),
          'key',
        ),
      )
    }

    if (invitations?.length) {
      const invitNotif = invitations.map(transformInvitationToNotification)
      items.push(...invitNotif)
    }

    return sortDate(items, 'date', 'DESC')
  }, [
    allReviews,
    buildReviewNotificationHref,
    invitations,
    tAccount,
    unreadOrderNotifications,
    user,
  ])

  return { notifications, streamUnreadError }
}
